/**
 * Conversión de la paleta clara del dashboard a su equivalente oscuro.
 *
 * El mismo hex significa cosas distintas según dónde se use (`#ffffff` es
 * superficie como fondo pero texto sobre botón de marca como color), por eso la
 * conversión depende del rol del color dentro del estilo.
 */

export type ColorRole = 'background' | 'text' | 'border' | 'shadow' | 'other';

/** Colores que nunca se convierten: acento de texto/icono, series y medallas. */
const PRESERVE = new Set(
  [
    '#c8b6b5', // acento de textos e iconos
    '#0ea5e9',
    '#ec4899',
    '#d4af37',
    '#b87333',
    'transparent',
  ].map((c) => c.toLowerCase())
);

/** Superficies y textos base del modo oscuro. */
export const DARK_BASE = {
  canvas: '#0f1115',
  surface: '#181b21',
  surfaceRaised: '#1d2027',
  surfaceSubtle: '#22262e',
  border: '#2f343d',
  borderStrong: '#3a4049',
  textPrimary: '#e8eaed',
  textBody: '#c7ccd4',
  textSecondary: '#9aa2ae',
  textMuted: '#7c8493',
  /** Acento de textos e iconos. */
  accent: '#C8B6B5',
  /** Acento de rellenos (botón +). */
  accentMuted: '#900020',
} as const;

/** Conversión aplicable a cualquier rol (semánticos, tintes, grises). */
const BASE_MAP: Record<string, string> = {
  // Grises Tailwind (texto por defecto)
  '#ffffff': DARK_BASE.textPrimary,
  '#fff': DARK_BASE.textPrimary,
  '#fefefe': DARK_BASE.textPrimary,
  '#fafafa': DARK_BASE.textPrimary,
  '#f9fafb': DARK_BASE.textPrimary,
  '#f8fafc': DARK_BASE.textPrimary,
  '#f5f5f5': DARK_BASE.textPrimary,
  '#f4f4f5': DARK_BASE.textPrimary,
  '#f3f4f6': DARK_BASE.textBody,
  '#f3f3f5': DARK_BASE.textBody,
  '#f1f5f9': DARK_BASE.textBody,
  '#e5e7eb': DARK_BASE.textBody,
  '#e2e8f0': DARK_BASE.textBody,
  '#e4e4e7': DARK_BASE.textBody,
  '#d1d5db': DARK_BASE.textSecondary,
  '#cbd5e1': DARK_BASE.textSecondary,
  '#9ca3af': DARK_BASE.textMuted,
  '#94a3b8': DARK_BASE.textMuted,
  '#6b7280': DARK_BASE.textSecondary,
  '#64748b': DARK_BASE.textSecondary,
  '#4b5563': DARK_BASE.textBody,
  '#475569': DARK_BASE.textBody,
  '#374151': DARK_BASE.textBody,
  '#334155': DARK_BASE.textBody,
  '#1f2937': DARK_BASE.textPrimary,
  '#111827': DARK_BASE.textPrimary,
  '#0f172a': DARK_BASE.textPrimary,
  '#030213': DARK_BASE.textPrimary,
  '#000000': DARK_BASE.textPrimary,
  '#000': DARK_BASE.textPrimary,

  // Semánticos: se aclaran para mantener contraste sobre fondo oscuro
  '#ef4444': '#f87171',
  '#dc2626': '#f05252',
  '#d4183d': '#f2557a',
  '#b91c1c': '#ef4444',
  '#991b1b': '#f87171',
  '#7f1d1d': '#fca5a5',
  '#10b981': '#34d399',
  '#059669': '#34d399',
  '#047857': '#34d399',
  '#065f46': '#6ee7b7',
  '#16a34a': '#4ade80',
  '#22c55e': '#4ade80',
  '#15803d': '#4ade80',
  '#3b82f6': '#60a5fa',
  '#2563eb': '#60a5fa',
  '#1d4ed8': '#60a5fa',
  '#1e40af': '#93c5fd',
  '#0284c7': '#38bdf8',
  '#0369a1': '#38bdf8',
  '#6366f1': '#818cf8',
  '#4f46e5': '#818cf8',
  '#4338ca': '#a5b4fc',
  '#7c3aed': '#a78bfa',
  '#f59e0b': '#fbbf24',
  '#d97706': '#fbbf24',
  '#ca8a04': '#facc15',
  '#b45309': '#fbbf24',
  '#92400e': '#fcd34d',
  '#be185d': '#f472b6',
  '#db2777': '#f472b6',

  // Fondos teñidos (tints Tailwind 50/100) → mismo tinte en oscuro
  '#eff6ff': '#17202f',
  '#dbeafe': '#1d2a40',
  '#bfdbfe': '#24354f',
  '#fef2f2': '#2a1719',
  '#fee2e2': '#3a1d20',
  '#fecaca': '#4a2427',
  '#ecfdf5': '#11241d',
  '#d1fae5': '#153024',
  '#a7f3d0': '#1b402f',
  '#f0fdf4': '#12241a',
  '#dcfce7': '#163021',
  '#fffbeb': '#2a2211',
  '#fef3c7': '#342917',
  '#fde68a': '#40331b',
  '#fcd34d': '#facc15',
  '#eef2ff': '#1a1c33',
  '#e0e7ff': '#232746',
  '#fdf2f8': '#2c1826',
  '#fce7f3': '#3a2032',
  '#f0f9ff': '#132430',
  '#e0f2fe': '#193445',
  // Marca: textos/iconos → #C8B6B5
  '#a20000': DARK_BASE.accent,
  '#7a0000': DARK_BASE.accent,
  '#900020': DARK_BASE.accent,
};

/** Overrides cuando el color es un fondo. */
const BACKGROUND_MAP: Record<string, string> = {
  '#ffffff': DARK_BASE.surface,
  '#fff': DARK_BASE.surface,
  '#fefefe': DARK_BASE.surface,
  '#fafafa': DARK_BASE.surfaceRaised,
  '#f9fafb': DARK_BASE.surfaceRaised,
  '#f8fafc': DARK_BASE.surfaceRaised,
  '#f5f5f5': DARK_BASE.surfaceRaised,
  '#f4f4f5': DARK_BASE.surfaceRaised,
  '#f3f4f6': DARK_BASE.surfaceSubtle,
  '#f3f3f5': DARK_BASE.surfaceSubtle,
  '#f1f5f9': DARK_BASE.surfaceSubtle,
  '#e5e7eb': '#2b3038',
  '#e2e8f0': '#2b3038',
  '#e4e4e7': '#2b3038',
  '#d1d5db': '#353b45',
  '#cbd5e1': '#353b45',
  '#9ca3af': '#4d535e',
  '#94a3b8': '#4d535e',
  '#6b7280': '#595f6b',
  '#64748b': '#595f6b',
  // Botones oscuros (primarios): se aclaran lo justo para verse sobre superficie oscura
  '#030213': '#4c5160',
  '#a20000': DARK_BASE.accentMuted,
  '#7a0000': DARK_BASE.accentMuted,
  '#900020': DARK_BASE.accentMuted,
  '#000000': '#4c5160',
  '#000': '#4c5160',
  '#111827': '#454b58',
  '#0f172a': '#454b58',
  '#1f2937': '#414752',
  '#374151': '#3b414c',
  '#334155': '#3b414c',
  '#4b5563': '#454b56',
};

/** Overrides cuando el color es un texto o icono. */
const TEXT_MAP: Record<string, string> = {
  // El blanco sobre botones de color debe seguir siendo blanco
  '#ffffff': '#ffffff',
  '#fff': '#ffffff',
  '#fefefe': '#ffffff',
  '#fafafa': '#f5f6f8',
  '#f9fafb': '#f5f6f8',
  '#f8fafc': '#f5f6f8',
  '#a20000': DARK_BASE.accent,
  '#7a0000': DARK_BASE.accent,
  '#900020': DARK_BASE.accent,
};

/** Overrides cuando el color es un borde. */
const BORDER_MAP: Record<string, string> = {
  '#ffffff': DARK_BASE.border,
  '#fff': DARK_BASE.border,
  '#fefefe': DARK_BASE.border,
  '#fafafa': DARK_BASE.border,
  '#f9fafb': DARK_BASE.border,
  '#f8fafc': DARK_BASE.border,
  '#f5f5f5': DARK_BASE.border,
  '#f4f4f5': DARK_BASE.border,
  '#f3f3f5': DARK_BASE.border,
  '#f3f4f6': DARK_BASE.border,
  '#f1f5f9': DARK_BASE.border,
  '#e5e7eb': DARK_BASE.border,
  '#e2e8f0': DARK_BASE.border,
  '#e4e4e7': DARK_BASE.border,
  '#d1d5db': DARK_BASE.borderStrong,
  '#cbd5e1': DARK_BASE.borderStrong,
  '#9ca3af': '#484e58',
  '#94a3b8': '#484e58',
  '#6b7280': '#525863',
  '#64748b': '#525863',
  '#4b5563': '#4a5059',
  '#475569': '#4a5059',
  '#374151': '#434952',
  '#334155': '#434952',
  '#1f2937': '#4a5059',
  '#111827': '#525863',
  '#0f172a': '#525863',
  '#030213': '#5a6070',
  '#000000': '#5a6070',
  '#000': '#5a6070',
  '#a20000': DARK_BASE.accent,
  '#7a0000': DARK_BASE.accent,
  '#900020': DARK_BASE.accent,
};

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_RE = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;

export function isColorValue(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v) return false;
  return v === 'transparent' || HEX_RE.test(v) || RGB_RE.test(v);
}

function expandHex(hex: string): string {
  const h = hex.trim().toLowerCase();
  if (h.length === 4) {
    return `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  if (h.length === 5) {
    return `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  if (h.length === 9) {
    return h.slice(0, 7);
  }
  return h;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = expandHex(hex);
  if (h.length !== 7) return null;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return { r, g, b };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number): string {
  const sn = Math.min(100, Math.max(0, s)) / 100;
  const ln = Math.min(100, Math.max(0, l)) / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => {
    const v = ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * v);
  };
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/** Conversión automática por luminosidad para colores fuera de la tabla. */
function autoDark(hex: string, role: ColorRole): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Colores saturados (marca, estados): se ajusta el brillo, no se invierte.
  if (s > 35) {
    // Tintes muy claros (fondos tipo Tailwind 50/100) pasan a tinte oscuro.
    if (l > 85) return hslToHex(h, Math.min(s, 45), 18);
    if (l < 45) return hslToHex(h, s, Math.min(72, l + 22));
    return hslToHex(h, s, Math.min(75, Math.max(55, l)));
  }

  // Grises y casi grises: se invierte la luminosidad.
  if (l > 92) return role === 'background' ? DARK_BASE.surface : DARK_BASE.textPrimary;
  if (l > 80) return role === 'background' ? DARK_BASE.surfaceSubtle : DARK_BASE.textBody;
  if (l < 12) return role === 'background' ? '#4c5160' : DARK_BASE.textPrimary;
  return hslToHex(h, Math.min(s, 18), 100 - l);
}

function mapRgbFunction(value: string, role: ColorRole): string {
  const m = RGB_RE.exec(value.trim());
  if (!m) return value;
  const r = Number(m[1]);
  const g = Number(m[2]);
  const b = Number(m[3]);
  const alpha = m[4] === undefined ? 1 : Number(m[4]);

  // Negros translúcidos: como velo siguen igual, pero como borde o texto
  // hay que invertirlos para que se vean sobre superficie oscura.
  if (alpha < 1 && r < 60 && g < 60 && b < 60) {
    if (role === 'border') return `rgba(255, 255, 255, ${Math.min(0.22, alpha + 0.04)})`;
    if (role === 'text') return `rgba(232, 234, 237, ${Math.min(1, alpha + 0.1)})`;
    return value;
  }

  const toHex = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, '0');
  const mapped = mapColorForRole(`#${toHex(r)}${toHex(g)}${toHex(b)}`, role);
  const rgbMapped = hexToRgb(mapped);
  if (!rgbMapped) return value;
  if (alpha >= 1) return `rgb(${rgbMapped.r}, ${rgbMapped.g}, ${rgbMapped.b})`;
  return `rgba(${rgbMapped.r}, ${rgbMapped.g}, ${rgbMapped.b}, ${alpha})`;
}

/** Equivalente oscuro de un color, según el rol que cumple en el estilo. */
export function mapColorForRole(color: string, role: ColorRole = 'other'): string {
  const raw = color.trim();
  if (!raw) return color;
  if (raw === 'transparent') return raw;

  // Las sombras siguen siendo negras en modo oscuro.
  if (role === 'shadow') return color;

  if (RGB_RE.test(raw)) return mapRgbFunction(raw, role);
  if (!HEX_RE.test(raw)) return color;

  const key = expandHex(raw);
  if (PRESERVE.has(key)) return color;

  const roleMap =
    role === 'background' ? BACKGROUND_MAP : role === 'text' ? TEXT_MAP : role === 'border' ? BORDER_MAP : null;

  const fromRole = roleMap?.[key];
  if (fromRole) return fromRole;

  const fromBase = BASE_MAP[key];
  if (fromBase) return fromBase;

  return autoDark(key, role);
}

/**
 * Bandera global del modo oscuro del dashboard. La mantiene `ThemeProvider` y
 * la lee `tc()` para colores sueltos (iconos, estilos en línea) sin obligar a
 * cada componente a consumir el contexto.
 */
let darkActive = false;

export function setDarkActive(active: boolean): void {
  darkActive = active;
}

export function isDarkActive(): boolean {
  return darkActive;
}

/** Color temático puntual: devuelve el original en claro y el mapeado en oscuro. */
export function tc(color: string, role: ColorRole = 'text'): string {
  return darkActive ? mapColorForRole(color, role) : color;
}

const BACKGROUND_KEYS = new Set(['backgroundColor', 'overlayColor']);
const TEXT_KEYS = new Set([
  'color',
  'tintColor',
  'placeholderTextColor',
  'textShadowColor',
  'selectionColor',
  'thumbColor',
]);
const BORDER_KEYS = new Set([
  'borderColor',
  'borderTopColor',
  'borderBottomColor',
  'borderLeftColor',
  'borderRightColor',
  'borderStartColor',
  'borderEndColor',
  'borderBlockColor',
  'borderInlineColor',
  'textDecorationColor',
  'outlineColor',
]);

/** Rol de color a partir del nombre de la propiedad de estilo. */
export function roleForStyleKey(key: string): ColorRole {
  if (BACKGROUND_KEYS.has(key)) return 'background';
  if (TEXT_KEYS.has(key)) return 'text';
  if (BORDER_KEYS.has(key)) return 'border';
  if (key === 'shadowColor') return 'shadow';
  return 'other';
}
